import { BASE_URL } from "./api";
/**
 * Utilitário centralizado para geração/impressão do PDF do documento.
 * Cabeçalho: logotipo + nome fantasia da entidade do documento.
 * Rodapé: data/hora (dd/MM/yyyy HH:mm) e usuário.
 * Os componentes chamam: gerarDocumentoPdf(doc, { nomeEntidade, logotipo, usuario }).
 */

/** Formata data para dd/MM/yyyy HH:mm */
function formatarDataHora(date) {
  const d = new Date(date);
  const dd = String(d.getDate()).padStart(2, "0");
  const MM = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${dd}/${MM}/${yyyy} ${hh}:${mm}`;
}

function escapeHtml(str) {
  const s = String(str);
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/**
 * Normaliza logotipo para uso em src da img (data URL).
 * @param {string|null} logotipo - Base64 ou já "data:image/..."
 */
function normalizarLogotipo(logotipo) {
  if (!logotipo || typeof logotipo !== "string") return null;
  if (logotipo.startsWith("data:")) return logotipo;
  return `data:image/png;base64,${logotipo}`;
}

/**
 * Monta o HTML do cabeçalho (logo + nome da entidade).
 */
function buildHeaderHtml(nomeEntidade, logotipoUrl) {
  const nome = nomeEntidade ? escapeHtml(String(nomeEntidade).trim()) : "";
  const hasLogo = Boolean(logotipoUrl);

  const logoBlock = hasLogo
    ? `<img src="${logotipoUrl}" alt="Logo" class="pdf-header-logo" />`
    : "";

  const nomeBlock = nome
    ? `<span class="pdf-header-nome">${nome}</span>`
    : "";

  if (!hasLogo && !nome) return "";

  return `
  <header class="pdf-header">
    <div class="pdf-header-inner">
      ${logoBlock}
      ${nomeBlock}
    </div>
  </header>`;
}


/**
 * Monta o HTML do rodapé (data/hora e usuário).
 */
function buildFooterHtml(dataHora, usuario) {
  const dt = escapeHtml(dataHora);
  const user = usuario ? escapeHtml(String(usuario).trim()) : "";
  return `
  <footer class="pdf-footer">
    <div class="pdf-footer-inner">
      <span>Impresso em: ${dt}</span>
      ${user ? `<span>Usuário: ${user}</span>` : ""}
    </div>
  </footer>`;
}

/**
 * Gera e abre a impressão do documento com cabeçalho (entidade + logotipo), tabela de campos e rodapé.
 * @param {Object} doc - Objeto do documento (todas as chaves não-função são exibidas)
 * @param {Object} [options] - Opções
 * @param {string} [options.nomeEntidade] - Nome fantasia da entidade (fallback se doc não tiver fantasia/empresa)
 * @param {string} [options.logotipo] - Logotipo em base64 ou data URL
 * @param {string} [options.usuario] - Nome ou e-mail do usuário para o rodapé
 */
/**
 * Retorna o HTML da pré-visualização do PDF (cabeçalho, tabela, rodapé, marca d'água).
 * Para imprimir, o chamador exibe esse HTML em um modal/iframe e o usuário usa Ctrl+P.
 * @returns {Promise<string|null>} HTML completo ou null se doc inválido
 */
async function gerarDocumentoPdf(doc, options = {}) {
  if (!doc || typeof doc !== "object") return null;

  const { nomeEntidade, logotipo, usuario } = options;
  const nomeFantasia = nomeEntidade || doc.fantasia || doc.empresa || "Entidade";
  const logotipoUrl = normalizarLogotipo(logotipo);
  const headerHtml = buildHeaderHtml(nomeFantasia, logotipoUrl);
  const dataHora = formatarDataHora(new Date());
  const footerHtml = buildFooterHtml(dataHora, usuario);

  // Dados do PDF: vêm EXCLUSIVAMENTE da rota GET /api/documentos/ListarParaPDF/:idDocumento.
  // Se não houver ID ou a rota não retornar dados válidos, não geramos relatório (retorna null).
  if (!doc.id) {
    return null;
  }

  let dataForPdf = null;
  try {
    const token = localStorage.getItem("authToken");
    const entidadeId = localStorage.getItem("entidade_id");
    const headers = { "Content-Type": "application/json" };
    if (token) headers.Authorization = `Bearer ${token}`;
    if (entidadeId) headers.id_entidade = entidadeId;

    const response = await fetch(
      `${BASE_URL}/api/documentos/ListarParaPDF/${doc.id}`,
      { headers }
    );

    if (response.ok) {
      const apiData = await response.json();
      const apiPayload = apiData?.data ?? apiData;
      if (apiPayload && typeof apiPayload === "object" && !Array.isArray(apiPayload)) {
        dataForPdf = apiPayload;
      }
    } else {
      // eslint-disable-next-line no-console
      console.error("Falha ao carregar dados para PDF:", response.status);
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error("Erro ao chamar ListarParaPDF:", err);
  }

  if (!dataForPdf) {
    return null;
  }

  const hasContent = (v) => {
    if (v === undefined || v === null || typeof v === "function") return false;
    if (typeof v === "string" && v.trim() === "") return false;
    if (Array.isArray(v) && v.length === 0) return false;
    if (typeof v === "object" && !Array.isArray(v) && Object.keys(v).length === 0) return false;
    return true;
  };

  // Mapa de rótulos: chave retornada pelo backend → label exibido no PDF
  const LABEL_MAP = {
    "ID DOCUMENTO":            "ID Documento",
    "TIPO DOCUMENTO":          "Tipo de Documento",
    "NUMERO/REFERENCIA":       "Número / Referência",
    "DATA INICIAL":            "Data Inicial",
    "DATA VENCIMENTO":         "Data de Vencimento",
    "RESPONSAVEL DO DOCUMENTO":"Responsável",
    "DIAS DE AVISO":           "Dias de Aviso",
    "FORNECEDOR":              "Fornecedor",
    "TIPO DE PAGAMENTO":       "Tipo de Pagamento",
    "INDICE DE CORRECAO":      "Índice de Correção",
    "FUNCIONARIO":             "Funcionário",
    "SITUACAO":                "Situação",
  };

  // Situação vira um badge colorido (mesmo vocabulário visual do GridPadrao:
  // verde para ativo/vigente, cinza para inativo/arquivado) em vez de texto puro.
  const badgeSituacao = (valor) => {
    const v = String(valor).trim().toUpperCase();
    const ativo = v === "ATIVO" || v === "VIGENTE" || v === "A" || v === "V";
    const classe = ativo ? "pdf-badge-success" : "pdf-badge-neutral";
    return `<span class="pdf-badge ${classe}">${escapeHtml(v)}</span>`;
  };

  // Uma linha da tabela: label na coluna Campo, display na coluna Valor (tudo em maiúsculas).
  const rowHtml = (label, display) => {
    const chaveNormalizada = String(label).trim().toUpperCase();
    const labelFinal = LABEL_MAP[chaveNormalizada] || label;
    const valorHtml =
      chaveNormalizada === "SITUACAO"
        ? badgeSituacao(display)
        : escapeHtml(String(display).toUpperCase());
    return `<tr><td class="pdf-cell-label">${escapeHtml(String(labelFinal).toUpperCase())}</td><td class="pdf-cell-value">${valorHtml}</td></tr>`;
  };

  // Campos normais: chaves do objeto (exceto campos_customizados, tratado abaixo).
  const rowsNormais = Object.keys(dataForPdf)
    .filter((k) => k !== "campos_customizados" && hasContent(dataForPdf[k]))
    .map((k) => {
      const v = dataForPdf[k];
      const display = typeof v === "object" ? JSON.stringify(v) : String(v);
      return rowHtml(k, display);
    })
    .join("");

  // Campos customizados: uma linha por item; coluna Campo = descricao, coluna Valor = valor.
  const customizados = Array.isArray(dataForPdf.campos_customizados) ? dataForPdf.campos_customizados : [];
  const rowsCustomizados = customizados
    .map((item) => {
      if (item == null || typeof item !== "object") return "";
      const display = item.valor != null ? String(item.valor).trim() : "";
      if (display === "") return ""; // não mostrar linha quando valor estiver vazio
      const label = item.descricao != null ? String(item.descricao) : (item.nome_campo != null ? String(item.nome_campo) : "");
      return rowHtml(label, display);
    })
    .join("");

  const rows = rowsNormais + rowsCustomizados;

  // Se não houver nenhum campo com conteúdo, não geramos relatório.
  if (!rows) {
    return null;
  }

  const html = `
<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="utf-8">
  <title>Documento - ${escapeHtml(String(doc.id ?? doc.nr_contrato ?? "Documento"))}</title>
  <style>
    /* Paleta e tipografia alinhadas ao Design System do app (Contexto/DesignSystem.md) —
       valores literais porque este HTML é gerado isolado, sem acesso às CSS vars do app. */
    * { box-sizing: border-box; }
    html, body {
      margin: 0;
      min-height: 100vh;
      font-family: system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
      font-size: 14px;
      color: #1e293b;
      background: #f1f5f9;
    }
    body {
      display: flex;
      flex-direction: column;
      padding: 32px;
      max-width: 900px;
      margin-left: auto;
      margin-right: auto;
      position: relative;
    }
    .pdf-main, .pdf-footer {
      position: relative;
      z-index: 1;
    }
    .pdf-main {
      flex: 1;
      display: flex;
      flex-direction: column;
      background: #ffffff;
      border-radius: 10px;
      box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08), 0 1px 2px rgba(15, 23, 42, 0.06);
      padding: 28px 32px 32px;
    }
    .pdf-header {
      border-bottom: 3px solid #0d6efd;
      padding-bottom: 16px;
      margin-bottom: 24px;
    }
    .pdf-header-inner {
      display: flex;
      align-items: center;
      gap: 16px;
      flex-wrap: wrap;
    }
    .pdf-header-logo {
      max-height: 56px;
      max-width: 200px;
      width: auto;
      height: auto;
      object-fit: contain;
      display: block;
    }
    .pdf-header-nome {
      font-size: 1.35rem;
      font-weight: 700;
      color: #0f172a;
      letter-spacing: 0.01em;
    }
    .pdf-table {
      border-collapse: separate;
      border-spacing: 0;
      width: 100%;
      font-size: 13px;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      overflow: hidden;
    }
    .pdf-table thead tr {
      background: #f8fafc;
    }
    .pdf-table th {
      padding: 11px 16px;
      text-align: left;
      font-weight: 700;
      font-size: 11.5px;
      letter-spacing: 0.4px;
      text-transform: uppercase;
      color: #64748b;
      border-bottom: 1px solid #e2e8f0;
    }
    .pdf-table td {
      padding: 10px 16px;
      border-bottom: 1px solid #eef1f5;
      vertical-align: top;
    }
    .pdf-table tbody tr:last-child td {
      border-bottom: none;
    }
    .pdf-table tbody tr:nth-child(even) {
      background: #f8fafc;
    }
    .pdf-cell-label {
      width: 220px;
      color: #475569;
      font-weight: 600;
    }
    .pdf-cell-value {
      word-break: break-word;
      color: #1e293b;
    }
    .pdf-badge {
      display: inline-block;
      padding: 3px 12px;
      border-radius: 50rem;
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.3px;
    }
    .pdf-badge-success {
      background: #f0fdf4;
      color: #15803d;
    }
    .pdf-badge-neutral {
      background: #f1f5f9;
      color: #475569;
    }
    .pdf-footer {
      margin-top: auto;
      padding-top: 12px;
      border-top: 1px solid #e2e8f0;
      font-size: 12px;
      color: #64748b;
      flex-shrink: 0;
    }
    .pdf-footer-inner {
      display: flex;
      flex-wrap: wrap;
      gap: 16px;
      align-items: center;
    }
    .pdf-footer-inner span:first-child {
      font-weight: 500;
    }
    @media print {
      @page { margin: 0; }
      html, body { min-height: 100vh; background: #ffffff; }
      body { padding: 16px; }
      .pdf-main {
        flex: 1;
        box-shadow: none;
        border-radius: 0;
        padding: 0;
      }
      .pdf-footer {
        margin-top: auto;
        position: relative;
      }
      .pdf-header {
        break-after: avoid;
        margin-bottom: 18px;
      }
      .pdf-header-logo {
        max-height: 48px;
        max-width: 180px;
      }
      .pdf-header-nome { font-size: 1.2rem; }
      .pdf-table { font-size: 12px; border-radius: 0; }
      .pdf-table td, .pdf-table th { padding: 7px 12px; }
      .pdf-footer { break-before: avoid; margin-top: 16px; font-size: 11px; }
    }
  </style>
</head>
<body>
  <div class="pdf-main">
  ${headerHtml}
  <table class="pdf-table">
    <thead><tr><th>Campo</th><th>Valor</th></tr></thead>
    <tbody>${rows}</tbody>
  </table>
  </div>
  ${footerHtml}
</body>
</html>`;

  return html;
}

export default gerarDocumentoPdf;
